import {
  Box,
  Hidden as HiddenBase,
  Logos,
  PrimaryNavigationButton,
  ProductHeader,
  ProductHeaderLogo as ProductHeaderLogoBase,
  ProductTitleText as ProductTitleTextBase,
} from '@cambridgeassessment/cambridge-ui';
import { IconButton } from '@material-ui/core';
import { HelpOutline } from '@material-ui/icons';
import React, { memo, useCallback } from 'react';
import { Link } from 'react-router-dom';
import useAuth from 'src/hooks/useAuth';
import { getCurrentEnvironment } from '../../env';
import { useAppContext } from '../AppContext';
import Banner from '../Banner/Banner';
import { useStyles } from '../Header/styles';
import routes from '../Pages/routes';
import UserMenu from '../UserMenu/UserMenu';

// Cambridge-UI was built for React 16/17 — cast to include children for React 18 compatibility
const Hidden = HiddenBase as React.ComponentType<{
  mdUp?: boolean;
  smDown?: boolean;
  children?: React.ReactNode;
}>;
const ProductHeaderLogo = ProductHeaderLogoBase as React.ComponentType<{
  'data-testid'?: string;
  children?: React.ReactNode;
}>;
const ProductTitleText = ProductTitleTextBase as React.ComponentType<{
  children?: React.ReactNode;
}>;

interface HeaderProps {
  onToggleSidebar?: () => void;
  isAuthenticated?: boolean;
}

const Header: React.FC<HeaderProps> = ({
  onToggleSidebar,
  isAuthenticated,
}) => {
  const { setAppState } = useAppContext();
  const onNavigateTo = useCallback((_e) => {
    setAppState((prevState) => ({
      ...prevState,
      currentPage: 'home_' + Date.now(),
    }));
  }, []);

  const { areAllOrgsStandard, getCurrentOrg } = useAuth();

  const environment = getCurrentEnvironment();

  const shouldDisplayBanner =
    ['development', 'dev', 'qa'].includes(environment) && !areAllOrgsStandard;

  const getBannerMessage = () => {
    const envMessages: { [key: string]: string } = {
      development: 'DEV Testing',
      dev: 'DEV Testing',
      qa: 'QA Testing',
    };

    const currentOrganisation = getCurrentOrg();
    const centerType = currentOrganisation?.centerType;

    const isViewingChildCentre = location.pathname.includes('/associate');
    const isViewingUser =
      location.pathname.includes('/users') ||
      location.pathname.includes('/admins');

    const message = envMessages[environment];
    if (message) {
      let bannerMessage = `${message}: This is a ${
        isViewingChildCentre ? 'child' : centerType
      } centre`;
      if (isViewingUser) {
        bannerMessage += `, User viewed belongs to ${
          isViewingChildCentre || centerType === 'child'
            ? 'child centre'
            : centerType === 'parent'
            ? 'both parent and child centres'
            : 'standard centre'
        }`;
      }
      return bannerMessage;
    }
    return '';
  };

  const classes = useStyles();

  const helpLink =
    'https://cambridgeorg.sharepoint.com/sites/Connect/SitePages/Centre-Hierachy-Help.aspx';

  return (
    <ProductHeader className={classes.headerWrapper} id="header">
      {onToggleSidebar && (
        <Hidden mdUp>
          <PrimaryNavigationButton onClick={onToggleSidebar} />
        </Hidden>
      )}
      <ProductHeaderLogo data-testid="link-button">
        <Link
          to={routes.Home.path}
          onClick={onNavigateTo}
          aria-label="Cambridge Universitypress and Assessment Logo"
        >
          <Logos.CambridgeUniversityPressAndAssessment />
        </Link>
      </ProductHeaderLogo>
      <ProductTitleText>My Cambridge</ProductTitleText>
      {shouldDisplayBanner && (
        <Banner>
          <Box display="flex" alignItems="center">
            {getBannerMessage()}
            <a
              href={helpLink}
              target="_blank"
              rel="noopener noreferrer"
              style={{ color: 'inherit', textDecoration: 'none' }}
            >
              <IconButton
                size="small"
                style={{
                  padding: 4,
                  marginLeft: 8,
                }}
                aria-label="Learn more about centre hierarchy"
              >
                <HelpOutline style={{ fontSize: 20 }} />
              </IconButton>
            </a>
          </Box>
        </Banner>
      )}
      <Box ml="auto">{isAuthenticated && <UserMenu />}</Box>
    </ProductHeader>
  );
};

export default memo(Header);
